import { useState } from 'react';
import { X } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

type VisibilityOption = 'show_name' | 'private';

interface SupporterVisibilityPickerProps {
  isOpen: boolean;
  // null = private (appears as "Anonymous"), string = display name shown publicly
  onConfirm: (displayName: string | null) => void;
  onClose: () => void;
}

const OPTIONS: { value: VisibilityOption; label: string; subtext: string }[] = [
  {
    value: 'show_name',
    label: 'Show my name',
    subtext: 'Your name appears on the campaign page',
  },
  {
    value: 'private',
    label: 'Stay private',
    subtext: 'You appear as "Anonymous" on the campaign page',
  },
];

/** @deprecated Use SupporterVisibilityPicker */
export { SupporterVisibilityPicker as HintNamePicker };

export function SupporterVisibilityPicker({ isOpen, onConfirm, onClose }: SupporterVisibilityPickerProps) {
  const [selected, setSelected] = useState<VisibilityOption>('show_name');
  const { user } = useUser();

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selected === 'private') {
      onConfirm(null);
    } else {
      const displayName = user?.fullName ?? user?.firstName ?? 'Supporter';
      onConfirm(displayName);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 mb-1">How should you appear?</h2>
        <p className="text-sm text-gray-500 mb-5">
          Choose whether your name shows on the campaign page.
        </p>

        {/* Option cards */}
        <div className="space-y-3 mb-6">
          {OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSelected(opt.value)}
              className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all ${
                selected === opt.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selected === opt.value ? 'border-indigo-500' : 'border-gray-300'
                  }`}
                >
                  {selected === opt.value && (
                    <span className="w-2 h-2 rounded-full bg-indigo-500" />
                  )}
                </span>
                <div>
                  <p className={`text-sm font-semibold ${selected === opt.value ? 'text-indigo-700' : 'text-gray-800'}`}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.subtext}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Action buttons */}
        <div className="space-y-2">
          <button
            onClick={handleConfirm}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl transition-colors"
          >
            Continue to Payment
          </button>
          <button
            onClick={onClose}
            className="w-full bg-transparent hover:bg-gray-50 text-gray-500 font-medium py-2.5 rounded-xl transition-colors text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
